import { ApiResponse } from '../types';
import { ApiClient } from './ApiClient';
import { getErrorResponse } from '../utils/errors';

export class JwtApiClient extends ApiClient {
  protected _refreshUrl: string | undefined | null = null;

  /*
    Gets the auth token expiry from a cookie.
      Returns the expiry date if it's found and can be parsed.
        Returns the earliest EPOC date if not, so that _shouldRefreshToken will always be true.
   */
  protected get tokenExpiry(): Date {
    const tokenInfoRegex = new RegExp('.auth.token_info=([^;]+)', 'g');
    try {
      const tokenInfo = JSON.parse(atob(tokenInfoRegex.exec(document.cookie)?.[1] ?? ''));
      const expiry = new Date(tokenInfo?.at_exp * 1000);

      return isNaN(expiry?.getTime()) ? new Date(0) : expiry;
    } catch (err) {
      return new Date(0);
    }
  }

  /*
    Returns true if the auth token has or is about to expire(d)
   */
  private get _shouldRefreshToken(): boolean {
    const refreshWindow = 2 * 60000; // If the token will be expired 2 minutes from now, we should refresh
    return Date.now() + refreshWindow >= this.tokenExpiry.getTime();
  }

  /*
      To be run before all authorised requests to unified customer / other APIs
      Checks if the current access token,
   */
  private async _refreshToken(): Promise<void> {
    if (!this._refreshUrl) return;

    await fetch(this._refreshUrl, {
      // This endpoint will automatically put the new token in a http-only cookie in the response
      headers: this.requestHeaders,
      method: 'POST',
      credentials: 'include',
    });
  }

  protected _shouldRetryRequest(responseStatusCode: number, haveRetried?: boolean) {
    switch (true) {
      case responseStatusCode >= 200 && responseStatusCode <= 299:
      case haveRetried: // have already retried
      case responseStatusCode === ApiClient.errorCodes.badRequest:
      case responseStatusCode === ApiClient.errorCodes.notFound:
      case responseStatusCode === ApiClient.errorCodes.serverError:
      case responseStatusCode === ApiClient.errorCodes.tooManyRequests:
        return false;
      default:
        return true;
    }
  }

  protected async _fetch<T>({
    path,
    method = 'GET',
    body = undefined,
    isRetry = false,
  }: {
    path: string;
    method?: string;
    body?: string | Record<string, any>;
    isRetry?: boolean;
  }): Promise<ApiResponse<T>> {
    try {
      if (this._refreshUrl && (isRetry || this._shouldRefreshToken)) {
        await this._refreshToken();
      }

      const response = await fetch(path, {
        method,
        headers: this.requestHeaders,
        credentials: this._refreshUrl ? 'include' : undefined,
        body: (body && typeof body !== 'string' ? JSON.stringify(body) : body ?? null) as string | null,
      });

      if (response.ok) {
        const result = await response.json().catch(() => {
          return {}; // Catching the .json(), if the body is empty 'response' will then be an empty object
        });

        return { data: result, statusCode: response.status };
      } else if (this._shouldRetryRequest(response.status, isRetry)) {
        return await this._fetch({ path, method, body, isRetry: true });
      }

      const responseText = await response.text();
      return { error: { message: responseText }, statusCode: response.status };
    } catch (error) {
      return getErrorResponse(error);
    }
  }
}
